import React from 'react';
import ReactDOM from 'react-dom';

import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import { type OrganizationAgentLocation } from '../Agent';
import { AgentDockConversation, type AgentDockConversationControls } from './AgentDockConversation';
import type { AgentConversationHistory } from './AgentHistoryMenu';
import { useReportAgentDockBounds } from './layout';
import { type AgentDockTab, getAgentDockTabId } from './state';

/**
 * The agent anchored in the layout's rail, where it stays from one page to the next.
 *
 * One conversation at a time, unlike the dock: there is no row of open ones here, so the header
 * carries a way to start a conversation instead of tabs, and its title is the way back to any of
 * them. The dock's row is given back to the content column while the agent lives here, so the two
 * are never both on screen.
 *
 * There is no minimize either. A panel is either anchored or it is not, and the way out is the
 * control that puts it back in the dock — where minimizing means something again, because there is
 * a bar left behind to bring it back from.
 */
export function AgentSidePanel(props: {
    /** Where in the layout to render: the rail's own slot for the agent. */
    slot: HTMLElement;
    /** Where the user is, so the agent can resolve "this site" without going looking. */
    location: OrganizationAgentLocation;
    /**
     * The conversation to show. Briefly null while one is being started — the panel is never
     * meant to sit empty, so the dock puts a fresh conversation in it as soon as it has none.
     */
    conversation: ({ tab: AgentDockTab; title: string } & AgentDockConversationControls) | null;
    /** The other conversations, which the header's title opens. */
    history: AgentConversationHistory;
    onNewConversation: () => void;
    onUnanchor: () => void;
}) {
    const { slot, location, conversation, history, onNewConversation, onUnanchor } = props;
    useReportAgentSidePanelBounds(slot);

    return ReactDOM.createPortal(
        <div
            data-testid="agentDock.panel"
            // The same card the page's side panel is given, so the two sitting in one column read
            // as two of the same thing rather than two different ideas.
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-0.5 border-control bg-base shadow-subtle"
        >
            {conversation ? (
                /* Keyed like the window's, so moving to another conversation scopes every piece of
                   chat state to it rather than carrying the last one's along. */
                <AgentDockConversation
                    key={getAgentDockTabId(conversation.tab)}
                    {...conversation}
                    history={history}
                    location={location}
                    onOpenConversation={history.onOpenConversation}
                    actions={
                        <Button
                            kind="blank"
                            size="small"
                            leadingIcon={Icon.Plus}
                            label="New conversation"
                            iconOnly
                            onPress={onNewConversation}
                        />
                    }
                    onUnanchor={onUnanchor}
                />
            ) : null}
        </div>,
        slot
    );
}

/**
 * Report where the panel is, for as long as it is mounted, to what has to keep clear of it.
 *
 * Measured off the slot the panel fills: it is there before the panel's first paint, and stays
 * put from one conversation to the next. Measured again as the slot resizes — the side panel
 * above it expanding widens the rail — and as the window does, which moves the rail without
 * resizing it.
 */
function useReportAgentSidePanelBounds(slot: HTMLElement) {
    const report = useReportAgentDockBounds();

    React.useLayoutEffect(() => {
        const measure = () => {
            const { left, right } = slot.getBoundingClientRect();
            report({ left, right });
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(slot);
        window.addEventListener('resize', measure);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [slot, report]);
}
